import React, { useMemo, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Anchor, Box, Divider, Flex, Menu, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
dayjs.extend(utc);
dayjs.extend(timezone);
import { SelectDatesRangeOptionMenu } from './select-dates-range-option-menu';
import { findMatchingDatesRangeOption, } from './date-range-options';
import classes from './date-range-filter.module.css';
const getSelectedLabel = (value) => {
    const hasSelectedRange = value.every((v) => v !== null);
    if (hasSelectedRange && value[0] && value[1]) {
        if (dayjs(value[0]).isSame(value[1], 'day')) {
            if (dayjs(value[0]).isSame(dayjs(), 'day')) {
                return 'Today';
            }
            return dayjs(value[0]).format('D MMM YYYY');
        }
        return value.map((v) => dayjs(v).format('D MMM YYYY')).join(' - ');
    }
    return undefined;
};
/**
 * A filter component that allows users to date ranges from a dropdown menu and displays the selection.
 */
const buildHolidayMap = (holidays) => {
    const map = new Map();
    for (const h of holidays) {
        const dateKey = dayjs(h.startDateTime).tz('Australia/Melbourne').format('YYYY-MM-DD');
        const existing = map.get(dateKey) ?? [];
        existing.push(h);
        map.set(dateKey, existing);
    }
    return map;
};
export const DateRangeFilterDropdown = ({ clearable = false, label, onChange: onChangeProp, value: valueProp, menuProps, rangeOptions, onClose, initialValue, onRangeOptionChange: onRangeOptionChangeProp, rangeOptionsTitle, bottomSection, numberOfColumns, publicHolidays = [], ...buttonProps }) => {
    const holidayMap = useMemo(() => buildHolidayMap(publicHolidays), [publicHolidays]);
    const [valueInternal, setValueInternal] = useState(initialValue ?? [null, null]);
    const [rangeOption, setRangeOption] = useState(() => {
        if (rangeOptions && initialValue) {
            return findMatchingDatesRangeOption(initialValue, rangeOptions);
        }
        return undefined;
    });
    const [date, setDate] = useState(valueProp?.[0] ?? new Date());
    const onChange = (val) => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(val);
        setValueInternal(val);
        onChangeProp?.(val, selectedLabel);
    };
    const onChangeRangeOption = (rangeOption) => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(rangeOption.range);
        setRangeOption(rangeOption);
        setValueInternal(rangeOption.range);
        setDate(rangeOption.range[0] ?? new Date());
        onRangeOptionChangeProp?.(rangeOption);
        onChangeProp?.(rangeOption.range, selectedLabel, rangeOption.presetKey);
    };
    const reset = () => {
        const selectedLabel = rangeOption?.label ?? getSelectedLabel(value);
        setValueInternal([null, null]);
        onChangeProp?.([null, null], selectedLabel);
        setRangeOption(undefined);
        onRangeOptionChangeProp?.(undefined);
    };
    const value = valueProp ?? valueInternal;
    useEffect(() => {
        if (rangeOptions && value) {
            const matchingRangeOption = findMatchingDatesRangeOption(value, rangeOptions);
            setRangeOption(matchingRangeOption);
            if (matchingRangeOption?.range[0]) {
                setDate(matchingRangeOption?.range[0]);
            }
            onRangeOptionChangeProp?.(matchingRangeOption);
        }
    }, [value]);
    const hasSelectedRange = value.every((v) => v !== null);
    const height = [320, clearable ? 30 : 0].reduce((acc, current) => acc + current, 0);
    const titleHeight = rangeOptionsTitle ? 44 : 0;
    const scrollAreaHeight = [height, -titleHeight].reduce((acc, current) => acc + current, 0);
    return (<>
      <Box h={height}>
        <Flex wrap='nowrap' align='stretch' gap={0}>
          {rangeOptions && rangeOptions.length > 0 && (<Flex className={classes.selectDateRangeMenu} h='100%' direction='column'>
              {rangeOptionsTitle && (<Box h={titleHeight}>
                  <Menu.Label fw={700} c='dark' px='md' py='xs' fz='sm'>
                    {rangeOptionsTitle}
                  </Menu.Label>
                  <Divider />
                </Box>)}
              <SelectDatesRangeOptionMenu onChange={onChangeRangeOption} rangeOptions={rangeOptions} value={rangeOption} scrollAreaHeight={scrollAreaHeight}/>
            </Flex>)}
          <Box h='100%' w={1} bg='red'/>
          <Stack mx='sm' my='xs' gap='sm'>
            <DatePicker type='range' numberOfColumns={numberOfColumns} value={value} allowSingleDateInRange onChange={(value) => onChange([value[0], value[1] ? dayjs(value[1]).endOf('day').toDate() : null])} date={date} onDateChange={setDate} getDayProps={holidayMap.size > 0
            ? (d) => {
                const key = dayjs(d).tz('Australia/Melbourne').format('YYYY-MM-DD');
                const holidays = holidayMap.get(key);
                if (holidays?.length) {
                    const title = holidays.map((h) => `PH: ${h.name} (${h.state})`).join(' / ');
                    return {
                        style: {
                            backgroundColor: 'var(--mantine-color-amber-1)',
                            border: '1px solid var(--mantine-color-amber-5)',
                        },
                        title,
                    };
                }
                return {};
            }
            : undefined} renderDay={holidayMap.size > 0
            ? (d) => {
                const key = dayjs(d).tz('Australia/Melbourne').format('YYYY-MM-DD');
                const holidays = holidayMap.get(key);
                const day = dayjs(d).date();
                if (holidays?.length) {
                    return (<span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {day}
                            <span style={{ fontSize: 9, color: 'var(--mantine-color-amber-7)', lineHeight: 1 }}>
                              PH
                            </span>
                          </span>);
                }
                return <span>{day}</span>;
            }
            : undefined}/>
            {clearable && (<Anchor role='button' onClick={reset} size='xs' c={!hasSelectedRange ? 'dimmed' : buttonProps.color} td={!hasSelectedRange ? 'none' : undefined} style={{ cursor: !hasSelectedRange ? 'not-allowed' : 'pointer' }} ta='right'>
                Clear Selection
              </Anchor>)}
          </Stack>
        </Flex>
      </Box>
      {bottomSection && <Box>{bottomSection}</Box>}
    </>);
};
